import PropTypes from 'prop-types';
import React, { useEffect, useId, useRef, useState } from 'react';
import { Input, Label, Modal, ModalBody, Spinner } from 'reactstrap';

// Single confirmation dialog for destructive or irreversible actions
// (delete, remove, reject, cancel, submit, ...). See CONFIRMATIONS.md.
//
// - `title` names the action as a question, `message` says what happens and
//   whether it can be undone, `subject` names the record it applies to.
// - Button labels name the action ("Delete tender"), never a bare "Yes"/"OK".
//   The dismiss button always reads `cancelLabel` ("Cancel" by default).
// - tone: danger (red, destructive) | warning (amber, hard to undo) |
//   primary (blue, final but not destructive, e.g. Submit).
// - `reasonLabel` renders a textarea whose text is passed to onConfirm(reason);
//   `reasonRequired` keeps the confirm button disabled until it is filled.
// - An async onConfirm keeps the dialog open with a spinner and blocks
//   dismissal and repeat clicks until it settles.

const TONES = {
  danger: { icon: 'ri-delete-bin-6-line', button: 'danger' },
  warning: { icon: 'ri-error-warning-line', button: 'warning' },
  primary: { icon: 'ri-question-line', button: 'primary' },
};

const ConfirmDialog = ({
  isOpen,
  tone = 'danger',
  icon,
  title,
  message,
  subject,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  reasonLabel,
  reasonPlaceholder = 'Explain why',
  reasonRequired = false,
  reasonMaxLength = 500,
  loading = false,
  onConfirm,
  onCancel,
}) => {
  const config = TONES[tone] || TONES.danger;
  const titleId = `hy-confirm-title-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const [pending, setPending] = useState(false);
  const [reason, setReason] = useState('');
  const pendingRef = useRef(false);
  const cancelRef = useRef(null);
  const busy = loading || pending;
  const hasReason = Boolean(reasonLabel);
  const trimmed = reason.trim();
  const canConfirm = !busy && (!hasReason || !reasonRequired || trimmed !== '');

  useEffect(() => {
    if (isOpen) setReason('');
  }, [isOpen]);

  const handleConfirm = async () => {
    if (pendingRef.current || !canConfirm) return;
    pendingRef.current = true;
    setPending(true);
    try {
      await onConfirm?.(hasReason ? trimmed : undefined);
    } finally {
      pendingRef.current = false;
      setPending(false);
    }
  };

  const handleCancel = () => {
    if (busy) return;
    onCancel?.();
  };

  return (
    <Modal
      fade
      centered
      isOpen={isOpen}
      toggle={handleCancel}
      backdrop='static'
      keyboard={!busy}
      role='alertdialog'
      aria-modal='true'
      labelledBy={titleId}
      // Focus lands on the safe choice so Enter/Space never confirms by accident.
      onOpened={() => cancelRef.current?.focus()}
    >
      <ModalBody className='p-4 p-sm-5 pb-sm-4'>
        <div className='text-center'>
          <div
            className={`hy-confirm-icon hy-confirm-icon--${tone} mb-4`}
            aria-hidden='true'
          >
            <i className={icon || config.icon} aria-hidden='true'></i>
          </div>
          <h4 className='fs-18 mb-2' id={titleId}>
            {title}
          </h4>
          {message && (
            <div className='text-muted mb-0' style={{ whiteSpace: 'pre-line' }}>
              {message}
            </div>
          )}
          {subject && <div className='hy-confirm-subject'>{subject}</div>}
        </div>
        {hasReason && (
          <div className='mt-4'>
            <Label for='hy-confirm-reason' className='form-label'>
              {reasonLabel}
              {reasonRequired && (
                <span className='text-danger ms-1' aria-hidden='true'>
                  *
                </span>
              )}
            </Label>
            <Input
              type='textarea'
              id='hy-confirm-reason'
              rows={3}
              maxLength={reasonMaxLength}
              placeholder={reasonPlaceholder}
              value={reason}
              disabled={busy}
              required={reasonRequired}
              aria-required={reasonRequired || undefined}
              onChange={e => setReason(e.target.value)}
            />
          </div>
        )}
        <div className='d-flex flex-wrap gap-2 justify-content-center mt-4'>
          <button
            type='button'
            ref={cancelRef}
            className='btn w-sm btn-light'
            disabled={busy}
            onClick={handleCancel}
            data-testid='confirm-cancel'
          >
            {cancelLabel}
          </button>
          <button
            type='button'
            className={`btn w-sm btn-${config.button}`}
            disabled={!canConfirm}
            aria-busy={busy || undefined}
            onClick={handleConfirm}
            data-testid='confirm-ok'
          >
            {busy && (
              <Spinner size='sm' className='me-2'>
                Working…
              </Spinner>
            )}
            {confirmLabel}
          </button>
        </div>
      </ModalBody>
    </Modal>
  );
};

ConfirmDialog.propTypes = {
  isOpen: PropTypes.bool,
  tone: PropTypes.oneOf(['danger', 'warning', 'primary']),
  icon: PropTypes.string,
  title: PropTypes.node.isRequired,
  message: PropTypes.node,
  subject: PropTypes.node,
  confirmLabel: PropTypes.string,
  cancelLabel: PropTypes.string,
  reasonLabel: PropTypes.string,
  reasonPlaceholder: PropTypes.string,
  reasonRequired: PropTypes.bool,
  reasonMaxLength: PropTypes.number,
  loading: PropTypes.bool,
  onConfirm: PropTypes.func,
  onCancel: PropTypes.func,
};

export default ConfirmDialog;
